import { useState } from 'react';
import { useApolloClient } from 'react-apollo';

import { IMAGE_SIZE_CODE } from '../constants/image-size-codes';
import { PhotoDownloadURL } from '../graphql/queries/photos';
import useOpenAlert from './useOpenAlert';
import { getErrorString } from '../util/normalize-errors';

/**
 * Photo downloading hook.
 *
 * @param {Object} options The options.
 * @param {string} options.photoId The RecordID of the Photo to download.
 * @param {IMAGE_SIZE_CODE} [options.imageSizeCode] The image size to download.
 * @param {boolean} [options.includeWatermark] Whether to download with a watermark or not.
 * @param {Function} [options.onDownload] Optional callback.
 * @returns {{handleDownload: ((function(): Promise<void>)|*), loading: boolean}}
 */
const usePhotoDownload = ({
  photoId,
  imageSizeCode = IMAGE_SIZE_CODE.LARGE2X,
  includeWatermark = false,
  onDownload,
}) => {
  const openErrorModal = useOpenAlert();
  const [loading, setLoading] = useState(false);
  const client = useApolloClient();

  const handleDownload = async ({ downloadOriginal = false } = {}) => {
    if (loading) {
      return;
    }

    setLoading(true);

    try {
      const {
        data: {
          photoDownloadUrls: { photo_urls: photoUrls },
        },
      } = await client.query({
        query: PhotoDownloadURL,
        variables: {
          photos: [
            {
              photo_id: photoId,
              photo_size: imageSizeCode.toUpperCase(),
              include_watermark: includeWatermark,
              download_original_photo: downloadOriginal,
            },
          ],
        },
        fetchPolicy: 'network-only',
      });
      const [downloadUrl] = photoUrls;

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.className = 'download-link';
      document.body.appendChild(link);
      link.click();
      onDownload?.({ downloadUrl });
    } catch (error) {
      openErrorModal({
        type: 'error',
        message: 'There was an error downloading the photo.',
        info: error?.request_id ?? getErrorString(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleDownload,
  };
};

export default usePhotoDownload;
